import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { Calendar } from 'react-date-range';
import { Card, CardBody, Col } from 'reactstrap';
import { useSelector } from 'react-redux';

import HeadingText from '../../../shared/components/form/HeadingText';
import StandardTableToolbar from '../../../shared/components/table-toolbar/StandardTableToolbar';
import { downloadImagesAsZip } from '../../modals/ContentModals/utils';
import { DEFAULT_TIMESTAMP_FORMAT } from '../../../constants/constants';
import { PTB_SORT_OPTIONS } from '../PetTrackingBoard/constants';
import { useGetFacilityPhotoGallery } from '../../../hooks/appointments/useGetFacilityPhotoGallery';
import {
  GALLERY_DEFAULT_PERIOD,
  GALLERY_DEFAULT_SORT_FIELD,
  GALLERY_DEFAULT_SORT_ORDER,
  GALLERY_DOWNLOAD_ZIP_NAME,
  GALLERY_MAX_DOWNLOAD_SELECTION,
  GALLERY_PERIOD,
  GALLERY_PERIOD_OPTIONS,
  GALLERY_SAMPLE_PHOTOS,
  formatGallerySingleDayLabel,
  getDefaultSingleDayDate,
} from './petPhotoGalleryConfig';
import {
  buildOrderedGalleryPhotos,
  formatGalleryPhotoSentAt,
  getCloudinaryGalleryTileUrl,
} from './petPhotoGalleryUtils';

function getGalleryPhotoKey(photo) {
  return `${photo.messageId}-${photo.galleryIndex}-${photo.url}`;
}

export default function PetPhotoGallery() {
  const facilityUtcOffset =
    useSelector((state) => state.user?.profile?.facility?.utcOffset) || 0;

  const [period, setPeriod] = useState(GALLERY_DEFAULT_PERIOD);
  const [singleDayDate, setSingleDayDate] = useState('');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState(GALLERY_DEFAULT_SORT_FIELD);
  const [sortOrder, setSortOrder] = useState(GALLERY_DEFAULT_SORT_ORDER);
  const [showUpdatedHint, setShowUpdatedHint] = useState(false);
  const [singleDayCalendarOpen, setSingleDayCalendarOpen] = useState(false);
  const [singleDayCalendarLeft, setSingleDayCalendarLeft] = useState(0);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedPhotoKeys, setSelectedPhotoKeys] = useState(
    () => new Set(),
  );
  const [isDownloading, setIsDownloading] = useState(false);
  const [showMaxSelectionHint, setShowMaxSelectionHint] = useState(false);

  const updatedHintTimerRef = useRef(null);
  const maxSelectionHintTimerRef = useRef(null);
  const singleDayWrapRef = useRef(null);
  const singleDayButtonRef = useRef(null);
  const singleDayCalendarPanelRef = useRef(null);

  const { data, refetch, isFetching } = useGetFacilityPhotoGallery({
    period,
    singleDayDate,
    utcOffset: facilityUtcOffset,
  });

  const photosForPeriod = useMemo(
    () =>
      buildOrderedGalleryPhotos({
        items: data?.items || [],
        sortField,
        sortOrder,
        search: '',
        utcOffset: facilityUtcOffset,
      }),
    [data?.items, sortField, sortOrder, facilityUtcOffset],
  );

  const hasRealPhotosForPeriod = photosForPeriod.length > 0;
  const isSampleMode = !isFetching && !hasRealPhotosForPeriod;

  const photos = useMemo(
    () =>
      buildOrderedGalleryPhotos({
        items: data?.items || [],
        sortField,
        sortOrder,
        search,
        utcOffset: facilityUtcOffset,
      }),
    [data?.items, sortField, sortOrder, search, facilityUtcOffset],
  );

  const selectedCount = selectedPhotoKeys.size;

  const exitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setSelectedPhotoKeys(new Set());
    setShowMaxSelectionHint(false);

    if (maxSelectionHintTimerRef.current) {
      clearTimeout(maxSelectionHintTimerRef.current);
      maxSelectionHintTimerRef.current = null;
    }
  }, []);

  const showMaxSelectionHintBriefly = useCallback(() => {
    setShowMaxSelectionHint(true);

    if (maxSelectionHintTimerRef.current) {
      clearTimeout(maxSelectionHintTimerRef.current);
    }

    maxSelectionHintTimerRef.current = setTimeout(() => {
      maxSelectionHintTimerRef.current = null;
      setShowMaxSelectionHint(false);
    }, 1500);
  }, []);

  const handleEnterSelectionMode = useCallback(() => {
    setSingleDayCalendarOpen(false);
    setSelectionMode(true);
    setSelectedPhotoKeys(new Set());
    setShowMaxSelectionHint(false);
  }, []);

  const updateSingleDayCalendarPosition = useCallback(() => {
    const wrap = singleDayWrapRef.current;
    const button = singleDayButtonRef.current;

    if (!wrap || !button) {
      return;
    }

    setSingleDayCalendarLeft(
      button.getBoundingClientRect().left - wrap.getBoundingClientRect().left,
    );
  }, []);

  const handlePeriodSegmentClick = useCallback(
    (nextPeriod) => {
      if (selectionMode) {
        return;
      }

      if (nextPeriod === GALLERY_PERIOD.singleDay) {
        setPeriod(GALLERY_PERIOD.singleDay);

        if (!singleDayDate) {
          setSingleDayDate(getDefaultSingleDayDate(facilityUtcOffset));
        }

        setSingleDayCalendarOpen(true);
        return;
      }

      setSingleDayCalendarOpen(false);
      setPeriod(nextPeriod);
    },
    [selectionMode, singleDayDate, facilityUtcOffset],
  );

  const handleCalendarSelect = useCallback(
    (selectedDate) => {
      if (selectionMode) {
        return;
      }

      setSingleDayDate(moment(selectedDate).format(DEFAULT_TIMESTAMP_FORMAT));
      setSingleDayCalendarOpen(false);
    },
    [selectionMode],
  );

const isSingleDayAtToday = useMemo(() => {
  const selected =
    singleDayDate &&
    moment(singleDayDate, DEFAULT_TIMESTAMP_FORMAT, true).isValid()
      ? singleDayDate
      : getDefaultSingleDayDate(facilityUtcOffset);

  return selected === getDefaultSingleDayDate(facilityUtcOffset);
}, [singleDayDate, facilityUtcOffset]);

const handleSingleDayShift = useCallback(
  (delta) => {
    if (selectionMode) {
      return;
    }

    const base =
      singleDayDate &&
      moment(singleDayDate, DEFAULT_TIMESTAMP_FORMAT, true).isValid()
        ? singleDayDate
        : getDefaultSingleDayDate(facilityUtcOffset);

    setSingleDayDate(
      moment(base, DEFAULT_TIMESTAMP_FORMAT, true)
        .add(delta, 'day')
        .format(DEFAULT_TIMESTAMP_FORMAT),
    );
    setSingleDayCalendarOpen(false);
  },
  [selectionMode, singleDayDate, facilityUtcOffset],
);

  const handlePhotoTileClick = useCallback(
    (photo) => {
      if (selectionMode) {
        const key = getGalleryPhotoKey(photo);

        setSelectedPhotoKeys((previousKeys) => {
          if (previousKeys.has(key)) {
            const nextKeys = new Set(previousKeys);
            nextKeys.delete(key);
            return nextKeys;
          }

          if (previousKeys.size >= GALLERY_MAX_DOWNLOAD_SELECTION) {
            showMaxSelectionHintBriefly();
            return previousKeys;
          }

          const nextKeys = new Set(previousKeys);
          nextKeys.add(key);
          return nextKeys;
        });
        return;
      }

      if (photo.url) {
        window.open(photo.url, '_blank');
      }
    },
    [selectionMode, showMaxSelectionHintBriefly],
  );

  const handleDownloadSelected = useCallback(async () => {
    const selectedUrls = photos
      .filter((photo) => selectedPhotoKeys.has(getGalleryPhotoKey(photo)))
      .map((photo) => photo.url)
      .filter(Boolean);

    if (!selectedUrls.length) {
      return;
    }

    setIsDownloading(true);

    try {
      await downloadImagesAsZip(selectedUrls, GALLERY_DOWNLOAD_ZIP_NAME);
      exitSelectionMode();
    } catch (error) {
      console.error('Error downloading selected photos:', error);
    } finally {
      setIsDownloading(false);
    }
  }, [photos, selectedPhotoKeys, exitSelectionMode]);

  useEffect(() => {
    return () => {
      if (updatedHintTimerRef.current) {
        clearTimeout(updatedHintTimerRef.current);
      }

      if (maxSelectionHintTimerRef.current) {
        clearTimeout(maxSelectionHintTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isSampleMode && selectionMode) {
      exitSelectionMode();
    }
  }, [isSampleMode, selectionMode, exitSelectionMode]);

  useEffect(() => {
    if (!singleDayCalendarOpen || selectionMode) {
      return undefined;
    }

    updateSingleDayCalendarPosition();

    window.addEventListener('resize', updateSingleDayCalendarPosition);
    return () => {
      window.removeEventListener('resize', updateSingleDayCalendarPosition);
    };
  }, [singleDayCalendarOpen, selectionMode, updateSingleDayCalendarPosition]);

  useEffect(() => {
    if (!singleDayCalendarOpen || selectionMode) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') {
        return;
      }

      event.preventDefault();
      setSingleDayCalendarOpen(false);
    };

    const handleMouseDown = (event) => {
      const wrap = singleDayWrapRef.current;

      if (
        singleDayCalendarPanelRef.current?.contains(event.target) ||
        wrap?.contains(event.target)
      ) {
        return;
      }

      setSingleDayCalendarOpen(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [singleDayCalendarOpen, selectionMode]);

  useEffect(() => {
    if (!selectionMode) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') {
        return;
      }

      event.preventDefault();
      exitSelectionMode();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectionMode, exitSelectionMode]);

  const handleRefresh = async () => {
    await refetch();

    setShowUpdatedHint(true);
    if (updatedHintTimerRef.current) {
      clearTimeout(updatedHintTimerRef.current);
    }
    updatedHintTimerRef.current = setTimeout(() => {
      updatedHintTimerRef.current = null;
      setShowUpdatedHint(false);
    }, 1500);
  };

  const handleSearchChange = useCallback(
    (nextSearch) => {
      if (selectionMode) {
        return;
      }

      setSearch(nextSearch);
    },
    [selectionMode],
  );

  const handleSortChange = useCallback(
    ({ field, order }) => {
      if (selectionMode) {
        return;
      }

      setSortField(field ?? GALLERY_DEFAULT_SORT_FIELD);
      setSortOrder(order ?? GALLERY_DEFAULT_SORT_ORDER);
    },
    [selectionMode],
  );

  return (
    <div
      className={classNames('patient-tracking pet-photo-gallery', {
        'pet-photo-gallery--selection-mode': selectionMode,
      })}
    >
      <Col md={12} lg={12}>
        <Card>
          <CardBody>
<div className="ptb-board-header">
  <div className="page-title-with-action ptb-board-header__title-row">
    <HeadingText text="Pet Photo Gallery" />
    <div
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <button
        type="button"
        className="btn btn-utility"
        onClick={handleRefresh}
        disabled={isFetching}
      >
        Refresh
      </button>
      {showUpdatedHint ? (
        <span
          style={{
            position: 'absolute',
            left: '100%',
            marginLeft: 16,
            fontSize: '12px',
            color: '#999',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
          }}
        >
          Updated!
        </span>
      ) : null}
    </div>
  </div>
  <div className="ptb-board-header__subtitle-row">
    <div className="ptb-board-header__subtitle">
      Photos from updates sent to Pet Parents
    </div>
  </div>
</div>

            <div className="ptb-board-toolbar pet-photo-gallery__toolbar">
              <div className="ptb-board-toolbar__controls">
                <div
                  className={classNames(
                    'pet-photo-gallery__toolbar-search-sort',
                    {
                      'pet-photo-gallery__toolbar-search-sort--disabled':
                        selectionMode,
                    },
                  )}
                >
                  <StandardTableToolbar
                    searchValue={search}
                    onSearchChange={handleSearchChange}
                    searchPlaceholder=""
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSortChange={handleSortChange}
                    sortOptions={PTB_SORT_OPTIONS}
                    filters={{}}
                    onFilterChange={() => {}}
                    onClearAllFilters={() => {}}
                    filterCategories={[]}
                  />
                </div>

                <div
                  className={classNames('pet-photo-gallery__period-controls', {
                    'pet-photo-gallery__period-controls--disabled':
                      selectionMode,
                  })}
                >
                  <div
                    className="table-toolbar__menu-wrap pet-photo-gallery__single-day-wrap"
                    ref={singleDayWrapRef}
                  >
                    <div className="table-toolbar__segmented">
                      {GALLERY_PERIOD_OPTIONS.map((option) => {
                        if (
                          option.value === GALLERY_PERIOD.singleDay &&
                          period === GALLERY_PERIOD.singleDay
                        ) {
                          return (
                            <div
                              key={option.value}
                              className={classNames(
                                'table-toolbar__segment',
                                'table-toolbar__segment--selected',
                                'pet-photo-gallery__single-day-segment',
                              )}
                            >
                              <button
                                ref={singleDayButtonRef}
                                type="button"
                                className="pet-photo-gallery__single-day-label"
                                disabled={selectionMode}
                                onClick={() =>
                                  handlePeriodSegmentClick(
                                    GALLERY_PERIOD.singleDay,
                                  )
                                }
                              >
                                Single Day
                              </button>
                              <span className="pet-photo-gallery__single-day-nav">
                                <button
                                  type="button"
                                  className="pet-photo-gallery__single-day-chevron"
                                  disabled={selectionMode}
                                  aria-label="Previous day"
                                  onClick={() => handleSingleDayShift(-1)}
                                >
                                  ‹
                                </button>
                                <button
                                  type="button"
                                  className="pet-photo-gallery__single-day-chevron"
                                  disabled={
                                    selectionMode || isSingleDayAtToday
                                  }
                                  aria-label="Next day"
                                  onClick={() => handleSingleDayShift(1)}
                                >
                                  ›
                                </button>
                              </span>
                            </div>
                          );
                        }

                        return (
                          <button
                            key={option.value}
                            ref={
                              option.value === GALLERY_PERIOD.singleDay
                                ? singleDayButtonRef
                                : null
                            }
                            type="button"
                            className={classNames('table-toolbar__segment', {
                              'table-toolbar__segment--selected':
                                period === option.value,
                            })}
                            disabled={selectionMode}
                            onClick={() =>
                              handlePeriodSegmentClick(option.value)
                            }
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>

                    {period === GALLERY_PERIOD.singleDay &&
                    singleDayCalendarOpen &&
                    !selectionMode ? (
                      <div
                        ref={singleDayCalendarPanelRef}
                        className="table-toolbar__panel pet-photo-gallery__single-day-calendar custom-date-picker-menu--compact"
                        role="dialog"
                        aria-label="Choose day"
                        style={{ left: singleDayCalendarLeft }}
                      >
                        <Calendar
                          date={
                            singleDayDate && moment(singleDayDate).isValid()
                              ? new Date(singleDayDate)
                              : new Date()
                          }
                          maxDate={new Date()}
                          onChange={handleCalendarSelect}
                        />
                      </div>
                    ) : null}
                  </div>

                  {period === GALLERY_PERIOD.singleDay && singleDayDate ? (
                    <span className="pet-photo-gallery__single-day-date">
                      {formatGallerySingleDayLabel(singleDayDate)}
                    </span>
                  ) : null}
                </div>

                {!isSampleMode ? (
                  <div className="pet-photo-gallery__selection-actions">
                    {!selectionMode ? (
                      <button
                        type="button"
                        className="btn pet-photo-gallery__selection-btn pet-photo-gallery__selection-btn--primary"
                        onClick={handleEnterSelectionMode}
                        disabled={isDownloading}
                      >
                        Select photos
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn pet-photo-gallery__selection-btn pet-photo-gallery__selection-btn--cancel"
                          onClick={exitSelectionMode}
                          disabled={isDownloading}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn pet-photo-gallery__selection-btn pet-photo-gallery__selection-btn--primary"
                          onClick={handleDownloadSelected}
                          disabled={selectedCount === 0 || isDownloading}
                        >
                          Download Selected ({selectedCount})
                        </button>
                        {showMaxSelectionHint ? (
                          <span className="pet-photo-gallery__selection-hint">
                            Maximum 10 photos per download.
                          </span>
                        ) : null}
                      </>
                    )}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="pet-photo-gallery__separator" aria-hidden="true" />

            {isSampleMode ? (
              <p className="pet-photo-gallery__sample-notice">
                <strong>Below are sample update photos.</strong>
                <br />
                No updates during this period included
                <br />
                photos taken and sent from the iPad app or
                <br />
                photos attached to updates sent from the Web app.
              </p>
            ) : null}

            <div
              className={classNames('pet-photo-gallery__grid', {
                'pet-photo-gallery__grid--sample': isSampleMode,
              })}
            >
              {isSampleMode
                ? GALLERY_SAMPLE_PHOTOS.map((sampleUrl, index) => (
                    <div
                      key={sampleUrl}
                      className="pet-photo-gallery__photo pet-photo-gallery__photo--sample"
                      aria-hidden="true"
                    >
                      <img
                        src={sampleUrl}
                        alt=""
                      />
                    </div>
                  ))
                : photos.map((photo) => {
                    const photoKey = getGalleryPhotoKey(photo);
                    const isSelected = selectedPhotoKeys.has(photoKey);

                    return (
                      <button
                        key={photoKey}
                        type="button"
                        className={classNames('pet-photo-gallery__photo', {
                          'pet-photo-gallery__photo--selected':
                            selectionMode && isSelected,
                        })}
                        onClick={() => handlePhotoTileClick(photo)}
                      >
                        <img
                          src={getCloudinaryGalleryTileUrl(photo.url)}
                          alt=""
                          loading="lazy"
                        />
                        {selectionMode ? (
                          <span
                            className={classNames(
                              'pet-photo-gallery__photo-select',
                              {
                                'pet-photo-gallery__photo-select--selected':
                                  isSelected,
                              },
                            )}
                            aria-hidden="true"
                          >
                            {isSelected ? (
                              <span
                                className="pet-photo-gallery__photo-select-check"
                                aria-hidden="true"
                              >
                                ✓
                              </span>
                            ) : null}
                          </span>
                        ) : null}
                        <span
                          className="pet-photo-gallery__photo-overlay"
                          aria-hidden="true"
                        >
                          <span className="pet-photo-gallery__photo-overlay-pet">
                            {photo.petName || '—'}
                          </span>
                          <span className="pet-photo-gallery__photo-overlay-parent">
                            {photo.parentName || '—'}
                          </span>
                          <span className="pet-photo-gallery__photo-overlay-sent">
                            {formatGalleryPhotoSentAt(
                              photo.dateTime,
                              facilityUtcOffset,
                            )}
                          </span>
                        </span>
                      </button>
                    );
                  })}
            </div>
          </CardBody>
        </Card>
      </Col>
    </div>
  );
}
